import { Box, Heading, TextBlock, ToggleSwitch } from "@bcc-ui/ui-components";
import classNames from "classnames";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import style from "./smart-grading-step.scss";
import { GradingWorkflowSelection } from "components/asset-management-system/custom-views/tabs/value/steps/smart-grading-step/GradingWorkflowSelection";
import { ValueComparisonChart } from "components/asset-management-system/custom-views/tabs/value/steps/smart-grading-step/ValueComparisonChart";
import { ValueStepCard } from "components/asset-management-system/custom-views/tabs/value/steps/value-step-card/ValueStepCard";
import { Step } from "components/asset-management-system/custom-views/tabs/value/ValueTab";
import { GradingStep, toStepperNumber, WorkflowForGrading } from "services/asset-management/AssetGradingService";
import { isStringBlank } from "utils/commonFunctions";

import testIds from "testIds.json";

interface Props {
    gradableAssetCount?: number;
    isSmartGradingEnabled: boolean;
    setIsSmartGradingEnabled: (value: boolean) => void;
    onStepChange: (step: Step) => void;
    onSmartGradingChange: (value: boolean) => void;
    setTemplateSelected: (selected: boolean) => void;
    setWorkflowUuid: (workflowUuid?: string) => void;
    workflowUuid?: string;
    workflows: WorkflowForGrading[];
    templates: WorkflowForGrading[];
    currentlySavedStep: number;
    isDisabled?: boolean;
}

export const SmartGradingStep = (props: Props) => {
    const { t } = useTranslation();
    const [isTogglePanelDisabled, setIsTogglePanelDisabled] = React.useState(false);

    // Set the first Blancco default grading workflow template as default selected
    useEffect(() => {
        const firstTemplate = props.templates[0];
        if (props.workflowUuid === undefined && firstTemplate) {
            props.setWorkflowUuid(firstTemplate.uuid);
        }
    }, []);

    useEffect(() => {
        setIsTogglePanelDisabled(
            isStringBlank(props.workflowUuid) ||
                props.currentlySavedStep === toStepperNumber(GradingStep.WORKFLOW_INTEGRATION_STARTED)
        );
    }, [props.workflowUuid, props.currentlySavedStep]);

    return (
        <>
            <ValueStepCard
                currentStepNumber={1}
                heading={t("AssetManagement.value.smartGrading.stepOne.title")}
                description={t("AssetManagement.value.smartGrading.stepOne.description", {
                    assetCount: props.gradableAssetCount ?? t("Common.all").toLowerCase(),
                })}
                leftSectionContent={
                    <Box columnLayout className={style.valueChartVisibility}>
                        <ValueComparisonChart />
                        <Box className={style.pillarLabelRow}>
                            <Heading variant="h2" className={classNames(style.pillarLabel, style.baselineLabel)}>
                                {t("AssetManagement.value.smartGrading.valueComparisonChart.baselineValue")}
                            </Heading>
                            <Heading variant="h2" className={classNames(style.pillarLabel, style.upliftLabel)}>
                                {t("AssetManagement.value.smartGrading.valueComparisonChart.potentialUplift")}
                            </Heading>
                            <Heading variant="h2" className={classNames(style.pillarLabel, style.maximumLabel)}>
                                {t("AssetManagement.value.smartGrading.valueComparisonChart.maximumValue")}
                            </Heading>
                        </Box>
                    </Box>
                }
                rightSectionContent={
                    <Box justifyContent="SpaceBetween" columnLayout={true} gap={"Sm"}>
                        <GradingWorkflowSelection
                            setTemplateSelected={props.setTemplateSelected}
                            setWorkflowUuid={props.setWorkflowUuid}
                            workflows={props.workflows}
                            templates={props.templates}
                            workflowUuid={props.workflowUuid}
                            isDisabled={props.isDisabled}
                            isTemplatePreferred={true}
                        />

                        <Box
                            justifyContent="SpaceBetween"
                            padding="Sm"
                            className={classNames(style.togglePanel, {
                                [style.disabled]: isTogglePanelDisabled || props.isDisabled,
                            })}
                        >
                            <Box horizontalAlignment={"Left"} columnLayout gap="Xxs">
                                <Heading variant={"SUBTITLE_1"} disableTopSpacing disableBottomSpacing>
                                    {t("AssetManagement.value.smartGrading.stepOne.enableTitle")}
                                </Heading>
                                <TextBlock disableBottomSpacing className={style.descriptionColor}>
                                    {t("AssetManagement.value.smartGrading.stepOne.enableDescription")}
                                </TextBlock>
                            </Box>
                            <ToggleSwitch
                                key={String(props.isSmartGradingEnabled)}
                                testId={testIds.workArea.asset.customView.value.smartGradingToggle}
                                type="checkbox"
                                name="smartGradingEnabled"
                                hideLabelSection
                                disabled={props.isDisabled}
                                defaultChecked={props.isSmartGradingEnabled}
                                onChange={(e) => {
                                    props.onSmartGradingChange(e.target.checked);
                                    if (e.target.checked) {
                                        props.onStepChange({
                                            activeStep: 2,
                                            viewedStep: 2,
                                            stepName: GradingStep.SMART_GRADING_STARTED,
                                        });
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                }
            />
        </>
    );
};
